import React, { useState, useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import Cart from '../Selection/Cart';
import Logo, { LOGO_SCROLL_DISTANCE } from './Logo';
import { goToPage, searchInput, changeLanguage, changeCountry, openCart } from '../../actions/environment';
import { addEvent, removeEvent, addClass, removeClass, hasClass } from '../../utils/dom';
import { translate, prefix } from '../../utils/locale';
import { urlify } from '../../utils/string';
import s from './Navigation.scss';


const Navigation = ({ navigation, setup, location, searchInputText, pagePaths, productCache, checkout, cartOpen, loggedIn, ...props }) => {
	const { selection = {}, account = {} } = checkout;
	const { totals = {} } = selection;
	const menuItems = useRef(null);
	const [mobileMenuOpen, toggleMobileMenu] = useState(false);
	const [toggledMenuParent, toggleMenuParent] = useState('');
	const [showHoverClear, toggleHoverClear] = useState(false);
	const [menuHeight, calculateMenuHeight] = useState(0);
	// The pin/shrink animation only makes sense on the start page - elsewhere the header has
	// different content above/around it, so keep the logo in its normal resting state there.
	const isStartPage = location.type === 'START';
	const barElement = useRef(null);
	// barOffset: where the bar sits in the page when not fixed - below the announcement bar if
	// there is one, otherwise 0. null until measured, leaving `top` unset so the fixed bar
	// renders at its static position (already below the announcement bar) on first paint.
	const [scrollState, updateScrollState] = useState({ pageScrollY: 0, headerIsFixed: false, barOffset: null });

	useEffect(() => {
		if (!isStartPage) return undefined;
		const handleScroll = () => {
			const pageScrollY = window.pageYOffset || document.documentElement.scrollTop;
			const headerIsFixed = hasClass(document.documentElement, 'u_headerIsFixed');
			updateScrollState((prevState) => {
				// Once the pre-existing reappearing sticky header takes over (see Header.jsx), its
				// container is itself position:fixed, so measuring the bar's parent no longer gives
				// the announcement bar's in-flow offset - it gives that fixed container's current
				// viewport position instead. Freeze barOffset at its last good (pre-sticky) value
				// rather than let it get corrupted; it's re-measured correctly once the sticky
				// header disengages and the container returns to normal flow.
				if (headerIsFixed) return { ...prevState, pageScrollY, headerIsFixed };
				const anchor = barElement.current && barElement.current.parentElement;
				return {
					pageScrollY,
					headerIsFixed,
					barOffset: anchor ? anchor.getBoundingClientRect().top + pageScrollY : 0,
				};
			});
		};
		handleScroll();
		addEvent(window, 'scroll.logo', () => {
			window.requestAnimationFrame(handleScroll);
		});
		addEvent(window, 'resize.logo', () => {
			window.requestAnimationFrame(handleScroll);
		});
		return () => {
			removeEvent(window, 'scroll.logo');
			removeEvent(window, 'resize.logo');
		};
	}, [isStartPage]);
	const { pageScrollY, headerIsFixed, barOffset } = scrollState;
	// Scroll distance measured from where the bar starts, so the pin/shrink animation begins once
	// the announcement bar (if any) has scrolled out of view.
	const barScrollY = pageScrollY - (barOffset || 0);

	const fixBackgroundScroll = (fixed) => {
		if (fixed) {
			const $html = document.querySelector('html');
			addClass(document.body, ['u_headerIsFixed', 'u_pageIsScrollingUp']);

			const scrollY = window.pageYOffset;
			document.body.style.position = 'fixed';
			document.body.style.top = `-${scrollY < 34 ? 34 : scrollY}px`;
			document.body.style.overflow = 'hidden';
			addClass(document.body, 'u_navigationOpen');

			setTimeout(() => {
				addClass($html, ['u_headerIsFixed', 'u_pageIsScrollingUp']);
				removeClass(document.body, ['u_headerIsFixed', 'u_pageIsScrollingUp']);
			}, 500);
		} else {
			const scrollY = document.body.style.top;
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.overflow = '';
			window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
			removeClass(document.body, 'u_navigationOpen');
		}
	};

	const	menuClicked = () => {
		toggleMobileMenu(false);
		fixBackgroundScroll(false);
		calculateMenuHeight(0);
		toggleMenuParent('');
		if (document.activeElement) document.activeElement.blur();
		if (searchInputText !== '') props.searchInput('');
	};

	const navigationClicked = () => {
		toggleHoverClear(true);
		setTimeout(() => {
			toggleHoverClear(false);
		}, 1000);
		menuClicked();
	};

	const navigationParentClicked = (e, key, hasLink = true) => {
		if (window.innerWidth <= 1100 && mobileMenuOpen) {
			if (key !== toggledMenuParent) {
				e.preventDefault();
				toggleMenuParent(key);
				return;
			}
			if (!hasLink) {
				// No page to navigate to - collapse this item's accordion rather than
				// closing the whole mobile menu
				e.preventDefault();
				toggleMenuParent('');
				return;
			}
		}
		if (!hasLink && window.innerWidth > 1100) {
			// Desktop submenu-only toggle has no page to navigate to - avoid clearing
			// hover state (which would close the submenu while still hovered)
			e.preventDefault();
			return;
		}
		navigationClicked();
	};

	const mobileMenuHandler = (e) => {
		if (cartOpen) {
			// Close cart menu
			if (typeof e !== 'boolean') e.preventDefault();
			fixBackgroundScroll(false);
			calculateMenuHeight(0);
			props.openCart(false);
		} else if (typeof e !== 'boolean') {
			// Toggle mobile menu
			e.preventDefault();
			toggleMenuParent('');
			toggleMobileMenu((prevState) => {
				fixBackgroundScroll(!prevState);
				calculateMenuHeight(prevState ? 0 : window.innerHeight);
				return !prevState;
			});
		} else {
			// e determines menu
			fixBackgroundScroll(e);
			calculateMenuHeight(e ? 0 : window.innerHeight);
			toggleMobileMenu(e);
			toggleMenuParent('');
		}
	};

	const openCartHandler = (e) => {
		if (window.innerWidth <= 1100 && !cartOpen) {
			e.preventDefault();
			if (mobileMenuOpen) {
				toggleMobileMenu(false);
				calculateMenuHeight(0);
				setTimeout(() => {
					props.openCart(true);
					calculateMenuHeight(window.innerHeight);
				}, 300);
			} else {
				fixBackgroundScroll(true);
				calculateMenuHeight(window.innerHeight);
				props.openCart(true);
			}
		}
	};

	const navigationClasses = [s.navigation];
	if (location.type === 'SEARCH') {
		navigationClasses.push(s.navigation__searchActive);
	}
	if (mobileMenuOpen) navigationClasses.push(s.navigation__menuOpen);
	if (cartOpen) navigationClasses.push(s.navigation__cartOpen);

	const navigationItemsStyle = {
		height: 'auto',
	};
	if (typeof window !== 'undefined' && window.innerWidth <= 600 && mobileMenuOpen) navigationItemsStyle.height = `${menuHeight}px`;

	const cartItemsStyle = {};
	if (typeof window !== 'undefined' && window.innerWidth <= 600) {
		if (cartOpen) cartItemsStyle.height = `${menuHeight}px`;
		else cartItemsStyle.height = '0px';
	}

	// While the pre-existing sticky header (u_headerIsFixed) hasn't taken over, the bar scrolls
	// with the page until it reaches the top of the viewport (following the announcement bar out
	// of view, if there is one), stays pinned there for the length of the logo's shrink animation,
	// then scrolls away at the same rate as the page - all as a single continuous `top` value so
	// there's no jump at either handover. Once the sticky header does take over, drop our own
	// positioning entirely and defer to it.
	const barClasses = [s.bar];
	let barStyle;
	if (isStartPage && !headerIsFixed) {
		barClasses.push(s.bar__fixed);
		if (barOffset !== null) {
			const top = barScrollY < 0 ? -barScrollY : Math.min(0, LOGO_SCROLL_DISTANCE - barScrollY);
			barStyle = { top: `${top}px` };
		}
	}

	return (
		<div ref={barElement} className={barClasses.join(' ')} style={barStyle}>
			<nav className={navigationClasses.join(' ')} style={navigationItemsStyle} aria-label="Navigation">
				<button type="button" className={s.navigation_mobileMenu} onClick={mobileMenuHandler} aria-label="Toggle mobile menu" aria-expanded={mobileMenuOpen} aria-controls="mobile-navigation-menu"><span /><span /><span /></button>
				<div className={[s.navigation_menu].join(' ')}>
					<div className={s.navigation_menu_overflow}>
						<div className={s.navigation_menu_overflow_items} id="mobile-navigation-menu" ref={menuItems}>
							<div className={s.navigation_menu_overflow_items_screen} />
							<div className={s.navigation_menu_overflow_items_background} />
							{
								navigation.header && [...navigation.header, ...navigation.support.slice(0, 1)].map((level1) => {
									const level1Uri = level1.uri ? prefix(setup, translate(setup, level1, 'uri')) : null;
									const level1Title = translate(setup, level1, 'title');
									const level1Key = level1.uri || urlify(level1Title);
									const toggled = toggledMenuParent === level1Key;
									return (
										<div key={level1Key} className={[s.navigation_menu_overflow_item, (toggled ? s.navigation_menu_overflow_item__expanded : null), (level1.subPages ? s.navigation_menu_overflow_item__hasSubItems : null)].join(' ')}>
											{
												level1Uri ? (
													<Link className={s.navigation_menu_overflow_item_link} to={level1Uri} onClick={(e) => navigationParentClicked(e, level1Key)}>
														<span>{ translate(setup, level1, 'title') }</span>
													</Link>
												) : (
													<button className={s.navigation_menu_overflow_item_link} type="button" onClick={(e) => navigationParentClicked(e, level1Key, false)}>
														<span>{ translate(setup, level1, 'title') }</span>
													</button>
												)
											}
											{
												level1.subPages && (
													<div className={s.navigation_menu_overflow_item_subMenuItems}>
														{
															level1.subPages.map((level2) => {
																const uri = translate(setup, level2, 'uri');
																if (/(http|mailto|tel)/.test(uri)) {
																	return <a className={s.navigation_menu_overflow_item_subMenuItem} href={uri} key={uri} target="_blank" rel="noopener noreferrer nofollow"><span>{ translate(setup, level2, 'title') }</span></a>;
																}
																return <Link key={uri} className={s.navigation_menu_overflow_item_subMenuItem} to={prefix(setup, uri)} onClick={navigationClicked}><span>{ translate(setup, level2, 'title') }</span></Link>;
															})
														}
													</div>
												)
											}
										</div>
									);
								})
							}
						</div>
						{ showHoverClear && <div className={s.navigation_menu_overflow_clear} /> }
					</div>
				</div>
			</nav>
			<Logo setup={setup} onClick={menuClicked} scrollY={isStartPage ? Math.max(0, barScrollY) : LOGO_SCROLL_DISTANCE} />
			<div className={s.functions}>
				{
					account.loggedIn && (
						<Link className={s.logOut} to={prefix(setup, 'logout')}>
							<span>{ translate(setup, 'accountSignOut') }</span>
						</Link>
					)
				}
				<Link className={s.search} to={prefix(setup, 'search')} aria-label="Search">
					<svg aria-hidden="true" focusable="false" width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
						<circle cx="6.75" cy="6.75" r="6.25" stroke="black" />
						<path d="M11.25 11.25L18 18" stroke="black" />
					</svg>
				</Link>
				<div className={[s.selection, (cartOpen ? s.selection__active : null)].join(' ')}>
					<Link className={s.selection_title} to={setup.consumerType === 'private' ? prefix(setup, 'selection') : prefix(setup, translate(setup, 'checkoutTradeLinkSelection'))} onClick={openCartHandler} aria-label="Shopping Cart">
						<svg aria-hidden="true" focusable="false" width="28" height="18" viewBox="0 0 28 18" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M1 1L5.10526 17H22.8947L27 1" stroke="black" />
						</svg>
						{
							!!totals.totalQuantity && parseInt(totals.totalQuantity, 10) > 0 && (
								<span className={s.selection_title_quantity}>{ totals.totalQuantity }</span>
							)
						}
					</Link>
					<div className={s.selection_cart} style={cartItemsStyle}>
						<Cart position="header" isOpen={cartOpen} toggleCartHandler={() => { fixBackgroundScroll(false); calculateMenuHeight(0); props.openCart(false); }} />
					</div>
					<div className={s.selection_cartScreen} />
				</div>
				<div className={s.functions_background} />
			</div>
		</div>
	);
};


const mapState = ({ environment, pageStatus, productCache, checkout }, props) => ({
	searchInputText: environment.searchInputText,
	pagePaths: pageStatus.paths,
	productCache,
	checkout,
	cartOpen: environment.properties.openCart,
	...props,
});

export default hot(module)(connect(mapState, { goToPage, searchInput, changeLanguage, changeCountry, openCart })(Navigation));
